// Basketball Connect status + the catalogue of every competition this year.
// Shown at the top of Admin -> BC Registrations.
//
// The organisation-wide sync creates one participant per Basketball Connect
// player and records every registration. Each competition gets a category;
// a Trial linked to a Swish IQ session has its registered players added to
// that session automatically.

import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { auditFetch } from '../auditFetch';
import { usePermissions } from '../context/PermissionsContext';
import CoachingProgramForm from '../components/CoachingProgramForm';

// Swish IQ's popups bring its stylesheet, so load them only when opened.
const SwishSessionModal = lazy(() => import('../swishiq/SwishSessionModal'));

const API_BASE = 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';
const CATEGORIES = ['Competition', 'Program', 'Trial', 'Other'];
const CATEGORY_COLOURS = {
  Competition: { bg: '#e6effd', fg: '#2a5fc4' },
  Program: { bg: '#e3f4ea', fg: '#1b7a43' },
  Trial: { bg: '#fde8f4', fg: '#b0287a' },
  Other: { bg: '#eeeef0', fg: '#48484f' },
};

function formatWhen(value) {
  if (!value) return 'never';
  try {
    return new Date(value).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  } catch {
    return String(value);
  }
}

export default function BCRegistrationsPanel({ canEdit }) {
  const permissions = usePermissions();
  const canEditPrograms = permissions.canEdit('admin', 'program-types');
  const [status, setStatus] = useState(null);
  // Edit popup for a linked item: { kind: 'program', programTypeId } or
  // { kind: 'swish', eventId }.
  const [editing, setEditing] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    const res = await auditFetch(`${API_BASE}/integrations/bc/registration-sync`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    setStatus(data);
    return data;
  }, []);

  const loadCatalog = useCallback(async () => {
    const catRes = await auditFetch(`${API_BASE}/integrations/bc/competitions-catalog`);
    if (catRes.ok) setCatalog(await catRes.json());
  }, []);

  useEffect(() => {
    loadStatus().catch((e) => setError(e.message));
    loadCatalog().catch(() => {});
    return () => clearInterval(pollRef.current);
  }, [loadStatus, loadCatalog]);

  // While a sweep runs, check every 5 seconds until it finishes.
  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const s = await loadStatus();
        if (!s.running) {
          clearInterval(pollRef.current);
          await loadCatalog();
          setMessage('Sync finished.');
        }
      } catch {
        clearInterval(pollRef.current);
      }
    }, 5000);
  }, [loadStatus, loadCatalog]);

  useEffect(() => {
    if (status?.running) startPolling();
  }, [status?.running, startPolling]);

  async function syncNow() {
    setError('');
    setMessage('');
    try {
      const res = await auditFetch(`${API_BASE}/integrations/bc/registration-sync`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setStatus((s) => ({ ...(s || {}), running: true }));
      setMessage('Syncing every registration from Basketball Connect. This can take a few minutes; you can leave this page.');
      startPolling();
    } catch (e) {
      setError(e.message);
    }
  }

  const [confirmUnlink, setConfirmUnlink] = useState(null);
  async function unlink(kind, link) {
    const key = `${kind}${link.id}`;
    if (confirmUnlink !== key) { setConfirmUnlink(key); return; }
    setConfirmUnlink(null);
    setError('');
    setMessage('');
    try {
      const url = kind === 's'
        ? `${API_BASE}/integrations/bc/swish-links/${link.id}`
        : `${API_BASE}/integrations/bc/mappings/${link.id}`;
      const res = await auditFetch(url, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      setMessage(kind === 's'
        ? `Unlinked SwishIQ Trials session "${link.eventName}". Players already in it stay; new registrants won't be added.`
        : `Unlinked coaching program "${link.programName}". Players already attached stay; new registrants won't be added.`);
      await loadCatalog();
    } catch (e) {
      setError(e.message);
    }
  }

  // Archive the program or session itself, and remove every competition link
  // to it so the sync stops adding players to it.
  async function archive(kind, link) {
    const key = `${kind}${link.id}`;
    if (confirmArchive !== key) { setConfirmArchive(key); return; }
    setConfirmArchive(null);
    setError('');
    setMessage('');
    try {
      const res = kind === 's'
        ? await auditFetch(`${API_BASE}/swishiq/events/${link.eventId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isArchived: true }),
          })
        : await auditFetch(`${API_BASE}/program-types/${link.programTypeId}`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      const others = catalog.flatMap((c) => (kind === 's'
        ? c.swishLinks.filter((l) => l.eventId === link.eventId)
        : c.programLinks.filter((l) => l.programTypeId === link.programTypeId)));
      for (const l of others) {
        await auditFetch(kind === 's'
          ? `${API_BASE}/integrations/bc/swish-links/${l.id}`
          : `${API_BASE}/integrations/bc/mappings/${l.id}`, { method: 'DELETE' });
      }
      setMessage(kind === 's'
        ? `Archived SwishIQ Trials session "${link.eventName}". It can be unarchived from SwishIQ Trials → Sessions.`
        : `Archived coaching program "${link.programName}". It can be restored from Admin → Coaching Programs.`);
      await loadCatalog();
    } catch (e) {
      setError(e.message);
    }
  }

  async function updateCompetition(comp, changes) {
    setSavingId(comp.id);
    setError('');
    setMessage('');
    try {
      const res = await auditFetch(`${API_BASE}/integrations/bc/competitions-catalog/${comp.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changes),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      if (data.trialPlayersAdded) setMessage(`Added ${data.trialPlayersAdded} registered player(s) to the SwishIQ Trials session.`);
      await loadCatalog();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  }

  const last = status?.last;
  // Registration sweep problems plus coaching-program attach failures.
  const problems = [...(status?.programProblems || []), ...(last?.errors || [])];
  const totals = last?.totals;
  const connected = status?.connection?.lastSignInStatus?.startsWith('ok');
  const counts = CATEGORIES.reduce((acc, c) => ({ ...acc, [c]: catalog.filter((x) => x.category === c).length }), {});
  const visible = catalog
    .filter((c) => filter === 'All' || c.category === filter)
    .filter((c) => !search.trim() || String(c.name || '').toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h3 style={cardH}>Basketball Connect</h3>
            <div style={{ fontSize: '0.9rem', lineHeight: 1.7 }}>
              <div>
                Connection:{' '}
                {status?.connection?.credentialsSaved === false ? (
                  <strong style={{ color: '#c62828' }}>No sign-in details saved (Admin → BC Integration)</strong>
                ) : connected ? (
                  <strong style={{ color: '#1b7a43' }}>Connected</strong>
                ) : (
                  <strong style={{ color: '#c62828' }}>Not connected</strong>
                )}
                {status?.connection?.lastSignIn && <span style={{ color: '#666' }}> · last sign-in {formatWhen(status.connection.lastSignIn)}</span>}
              </div>
              {!connected && status?.connection?.lastSignInStatus && (
                <div style={{ color: '#c62828', fontSize: '0.85rem' }}>{status.connection.lastSignInStatus}</div>
              )}
              <div>
                Last full sync: <strong>{last?.finishedAt ? formatWhen(last.finishedAt) : 'not run yet'}</strong>
                {last?.lastSuccessAt && last.lastSuccessAt !== last.finishedAt && (
                  <span style={{ color: '#666' }}> · last successful {formatWhen(last.lastSuccessAt)}</span>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <button type="button" onClick={syncNow} disabled={status?.running} style={{ ...btnPrimary, opacity: status?.running ? 0.6 : 1 }}>
              {status?.running ? 'Syncing…' : 'Sync all registrations now'}
            </button>
          )}
        </div>

        {totals && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8, marginTop: 12 }}>
            {[
              ['Registrations read', totals.registrationsFetched],
              ['New players', totals.participantsCreated],
              ['Linked to existing', totals.participantsLinkedByName],
              ['Profiles filled in', totals.participantsUpdated],
              ['New registrations', totals.registrationsNew],
              ['Changed', totals.registrationsUpdated],
              ['Withdrawn', totals.registrationsWithdrawn],
              ['Duplicates merged', totals.merged],
              ['Competitions', totals.competitions],
              ['Added to trials', totals.trialPlayersAdded],
            ].map(([label, value]) => (
              <div key={label} style={{ background: '#fafafa', borderRadius: 6, padding: '6px 10px' }}>
                <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>{value ?? 0}</div>
                <div style={{ fontSize: '0.75rem', color: '#666' }}>{label}</div>
              </div>
            ))}
          </div>
        )}
        {problems.length > 0 && (
          <div style={{ marginTop: 10, background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '8px 10px', fontSize: '0.85rem' }}>
            <strong>{problems.length} problem(s) in the last sync:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {problems.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        )}
        {message && <div style={{ marginTop: 10, color: '#1b5e20', fontSize: '0.9rem' }}>{message}</div>}
        {error && <div style={{ marginTop: 10, color: '#c62828', fontSize: '0.9rem' }}>{error}</div>}
      </div>

      <div style={card}>
        <h3 style={cardH}>All competitions this year</h3>
        <p style={{ color: '#666', fontSize: '0.85rem', marginTop: 0 }}>
          Every Basketball Connect competition, with its registrations recorded against each player. Categories are
          guessed from the name; change any that are wrong. Create the SwishIQ side straight from a line: a
          <strong> SwishIQ Trials session</strong> for a Trial, or a <strong>coaching program</strong> for a Program, for the
          whole competition or one registration group. Registered players are added automatically, and new registrants
          keep flowing in on every sync.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          {['All', ...CATEGORIES].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setFilter(c)}
              style={{
                border: '1.5px solid #a4161a', borderRadius: 999, padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                background: filter === c ? '#a4161a' : 'white', color: filter === c ? 'white' : '#a4161a',
              }}
            >
              {c}{c !== 'All' ? ` (${counts[c] || 0})` : ` (${catalog.length})`}
            </button>
          ))}
          <input
            placeholder="Search competitions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginLeft: 'auto', padding: '0.35rem 0.6rem', border: '1px solid #ccc', borderRadius: 4, minWidth: 200 }}
          />
        </div>
        {catalog.length === 0 ? (
          <p style={{ color: '#888', fontSize: '0.9rem' }}>No competitions yet. Run the sync above to load them from Basketball Connect.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#fafafa', borderBottom: '2px solid #e0e0e0' }}>
                  <th style={th}>Competition</th>
                  <th style={th}>Category</th>
                  <th style={th}>Registrations</th>
                  <th style={th}>In SwishIQ</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((c) => {
                  const colour = CATEGORY_COLOURS[c.category] || CATEGORY_COLOURS.Other;
                  return (
                    <React.Fragment key={c.id}>
                    <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={td}>{c.name || c.competitionUniqueKey}</td>
                      <td style={td}>
                        {canEdit ? (
                          <select
                            value={c.category}
                            disabled={savingId === c.id}
                            onChange={(e) => updateCompetition(c, { category: e.target.value })}
                            style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid #ccc', background: colour.bg, color: colour.fg, fontWeight: 600 }}
                          >
                            {CATEGORIES.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                          </select>
                        ) : (
                          <span style={{ background: colour.bg, color: colour.fg, borderRadius: 999, padding: '2px 9px', fontWeight: 700, fontSize: '0.75rem' }}>{c.category}</span>
                        )}
                        {c.categorySetBy === 'auto' && <span style={{ color: '#999', fontSize: '0.75rem', marginLeft: 6 }}>guessed</span>}
                      </td>
                      <td style={td}>
                        {c.activeCount} active{c.registrationCount !== c.activeCount ? ` · ${c.registrationCount - c.activeCount} withdrawn` : ''}
                      </td>
                      <td style={td}>
                        {c.swishLinks.map((l) => (
                          <div key={`s${l.id}`} style={linkRow}>
                            <span>SwishIQ Trials: <strong>{l.eventName}</strong>{l.registrationGroup ? ` · ${l.registrationGroup}` : ' · all groups'}</span>
                            {canEdit && (
                              <>
                                <button type="button" style={editBtn} onClick={() => setEditing({ kind: 'swish', eventId: l.eventId })}>Edit</button>
                                <button type="button" style={unlinkBtn} onClick={() => archive('s', l)}>
                                  {confirmArchive === `s${l.id}` ? 'Confirm archive' : 'Archive'}
                                </button>
                                <button type="button" style={unlinkBtn} onClick={() => unlink('s', l)}>
                                  {confirmUnlink === `s${l.id}` ? 'Confirm unlink' : 'Unlink'}
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                        {c.programLinks.map((l) => (
                          <div key={`p${l.id}`} style={linkRow}>
                            <span>Program: <strong>{l.programName}</strong>{l.registrationGroup ? ` · ${l.registrationGroup}` : ' · all groups'}</span>
                            {canEdit && canEditPrograms && (
                              <>
                                <button type="button" style={editBtn} onClick={() => setEditing({ kind: 'program', programTypeId: l.programTypeId })}>Edit</button>
                                <button type="button" style={unlinkBtn} onClick={() => archive('p', l)}>
                                  {confirmArchive === `p${l.id}` ? 'Confirm archive' : 'Archive'}
                                </button>
                              </>
                            )}
                            {canEdit && (
                              <button type="button" style={unlinkBtn} onClick={() => unlink('p', l)}>
                                {confirmUnlink === `p${l.id}` ? 'Confirm unlink' : 'Unlink'}
                              </button>
                            )}
                          </div>
                        ))}
                        {!c.swishLinks.length && !c.programLinks.length && (
                          <span style={{ color: '#999' }}>{c.category === 'Competition' || c.category === 'Other' ? 'Recorded on player profiles' : 'Nothing created yet'}</span>
                        )}
                        {canEdit && (c.category === 'Trial' || c.category === 'Program') && creatingFor?.id !== c.id && (
                          <div>
                            <button type="button" style={createBtn} onClick={() => setCreatingFor(c)}>
                              {c.category === 'Trial' ? '+ Create or link SwishIQ Trials session' : '+ Create or link coaching program'}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {creatingFor?.id === c.id && (
                      <tr>
                        <td colSpan={4} style={{ padding: '0 10px 12px' }}>
                          <CreateFromCompetition
                            comp={c}
                            canCreateProgram={canEditPrograms}
                            onCancel={() => setCreatingFor(null)}
                            onCreated={async (msg) => { setCreatingFor(null); setMessage(msg); await loadCatalog(); }}
                          />
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {editing?.kind === 'program' && (
        <CoachingProgramForm
          programId={editing.programTypeId}
          onCancel={() => setEditing(null)}
          onSaved={async (saved) => { setEditing(null); setMessage(saved.message); await loadCatalog(); }}
        />
      )}
      {editing?.kind === 'swish' && (
        <Suspense fallback={null}>
          <SwishSessionModal
            eventId={editing.eventId}
            onClose={async () => { setEditing(null); await loadCatalog(); }}
          />
        </Suspense>
      )}
    </>
  );
}

// Inline panel for one competition: choose the registration group, then
// either open the full create popup (the Admin → Coaching Programs form, or
// Swish IQ's New session popup) or link something that already exists.
function CreateFromCompetition({ comp, canCreateProgram, onCancel, onCreated }) {
  const isTrial = comp.category === 'Trial';
  const [mode, setMode] = useState(isTrial || canCreateProgram ? 'new' : 'existing'); // 'new' | 'existing'
  const [existingOptions, setExistingOptions] = useState(null);
  const [existingId, setExistingId] = useState('');
  const [groups, setGroups] = useState(null);
  const [divisionId, setDivisionId] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    auditFetch(`${API_BASE}/integrations/bc/competitions-catalog/${comp.id}/registration-groups`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => setGroups(Array.isArray(rows) ? rows : []))
      .catch(() => setGroups([]));
  }, [comp.id]);

  useEffect(() => {
    if (mode !== 'existing' || existingOptions) return;
    const url = isTrial ? `${API_BASE}/integrations/bc/swish-sessions` : `${API_BASE}/program-types`;
    auditFetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => {
        const list = (Array.isArray(rows) ? rows : [])
          .filter((r) => !r.isArchived)
          .map((r) => ({ id: r.id, name: r.name }))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setExistingOptions(list);
      })
      .catch(() => setExistingOptions([]));
  }, [mode, existingOptions, isTrial]);

  const group = (groups || []).find((g) => String(g.divisionId) === String(divisionId)) || null;
  const suggestedName = group ? `${comp.name} – ${group.name}` : comp.name || '';
  const subtitle = `From Basketball Connect: ${comp.name}${group ? ` · ${group.name}` : ' · all registration groups'}`;

  function mappingBody(programTypeId) {
    return JSON.stringify({
      externalCompetitionId: comp.competitionUniqueKey,
      externalCompetitionName: comp.name,
      externalDivisionId: group ? group.divisionId : null,
      externalDivisionName: group ? group.name : null,
      programTypeId,
      autoSync: true,
    });
  }

  async function linkExisting() {
    if (!existingId) { setError(isTrial ? 'Choose a SwishIQ Trials session.' : 'Choose a coaching program.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = isTrial
        ? await auditFetch(`${API_BASE}/integrations/bc/competitions-catalog/${comp.id}/swish-link`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ swishEventId: Number(existingId), divisionId: group ? group.divisionId : null, divisionName: group ? group.name : null }),
          })
        : await auditFetch(`${API_BASE}/integrations/bc/mappings`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: mappingBody(Number(existingId)),
          });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      const chosen = (existingOptions || []).find((o) => String(o.id) === String(existingId));
      onCreated(isTrial
        ? `Linked SwishIQ Trials session "${data.eventName || chosen?.name}" (${data.registeredPlayers} registered player(s)${data.trialPlayersAdded ? `, ${data.trialPlayersAdded} added to trial days` : ''}).`
        : `Linked coaching program "${data.programTypeName || chosen?.name}". Registered players attach on the next sync.`);
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  }

  // The program is saved by the popup; this links it to the competition.
  async function linkNewProgram(saved) {
    setPopupOpen(false);
    setSaving(true);
    setError('');
    try {
      const res = await auditFetch(`${API_BASE}/integrations/bc/mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: mappingBody(Number(saved.id)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || data.message || `HTTP ${res.status}`);
      onCreated(`${saved.message} Linked to ${comp.name}${group ? ` (${group.name})` : ''}; registered players attach on the next sync.`);
    } catch (e) {
      setError(`Coaching program "${saved.name}" was created but couldn't be linked: ${e.message}. Use "Link existing" to link it.`);
      setSaving(false);
    }
  }

  async function createSwishSession({ name, gender }) {
    const res = await auditFetch(`${API_BASE}/integrations/bc/competitions-catalog/${comp.id}/swish-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, divisionId: group ? group.divisionId : null, divisionName: group ? group.name : null, gender }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) throw new Error(data.error || data.message || `HTTP ${res.status}`);
    return {
      eventId: data.eventId,
      message: `Created SwishIQ Trials session "${name}" with ${data.registeredPlayers} registered player(s)`
        + (data.trialPlayersAdded ? `; ${data.trialPlayersAdded} added to trial days.` : '.'),
    };
  }

  function go() {
    if (mode === 'existing') { linkExisting(); return; }
    setError('');
    setPopupOpen(true);
  }

  const noun = isTrial ? 'SwishIQ Trials session' : 'coaching program';

  return (
    <div style={{ background: '#f7f9fc', border: '1px solid #dbe4f0', borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <strong>{comp.name}</strong>
        {(isTrial || canCreateProgram) && (
          <label style={{ fontSize: '0.85rem' }}>
            <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} /> Create new {noun}
          </label>
        )}
        <label style={{ fontSize: '0.85rem' }}>
          <input type="radio" checked={mode === 'existing'} onChange={() => setMode('existing')} /> Link existing {noun}
        </label>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
        <label style={lbl}>
          Registration group
          <select value={divisionId} onChange={(e) => setDivisionId(e.target.value)} style={inp}>
            <option value="">All registration groups ({comp.activeCount} active)</option>
            {(groups || []).map((g) => (
              <option key={g.divisionId} value={g.divisionId}>{g.name} ({g.activeCount} active)</option>
            ))}
          </select>
          {groups && groups.length === 0 && <span style={hint}>No registration groups synced yet; run the sync to load them.</span>}
          {isTrial && mode === 'new' && <span style={hint}>Becomes the session's Membership Division.</span>}
        </label>
        {mode === 'existing' && (
          <label style={lbl}>
            {isTrial ? 'SwishIQ Trials session' : 'Coaching program'}
            <select value={existingId} onChange={(e) => setExistingId(e.target.value)} style={inp}>
              <option value="">{existingOptions === null ? 'Loading…' : 'Choose…'}</option>
              {(existingOptions || []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </label>
        )}
      </div>
      {error && <div style={{ color: '#c62828', marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button type="button" onClick={go} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.6 : 1 }}>
          {saving
            ? (mode === 'existing' ? 'Linking…' : 'Saving…')
            : mode === 'existing' ? `Link ${noun}` : `Create ${noun}…`}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} style={{ padding: '0.5rem 1rem', background: 'white', border: '1px solid #bbb', borderRadius: 4, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
      {popupOpen && !isTrial && (
        <CoachingProgramForm
          initialName={suggestedName}
          subtitle={subtitle}
          onCancel={() => setPopupOpen(false)}
          onSaved={linkNewProgram}
        />
      )}
      {popupOpen && isTrial && (
        <Suspense fallback={null}>
          <SwishSessionModal
            create={{ initialName: suggestedName, groupName: group ? group.name : '', subtitle, onCreate: createSwishSession }}
            onClose={(msg) => { setPopupOpen(false); if (msg) onCreated(msg); }}
          />
        </Suspense>
      )}
    </div>
  );
}

const card = { background: 'white', border: '1px solid #e0e0e0', borderRadius: 8, padding: '1.25rem 1.5rem', marginBottom: '1rem' };
const linkRow = { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 2 };
const unlinkBtn = { padding: '1px 8px', fontSize: '0.72rem', background: 'white', color: '#c62828', border: '1px solid #e0a0a0', borderRadius: 999, cursor: 'pointer' };
const editBtn = { padding: '1px 8px', fontSize: '0.72rem', background: 'white', color: '#1565c0', border: '1px solid #90b4e0', borderRadius: 999, cursor: 'pointer' };
const createBtn = { marginTop: 6, padding: '3px 10px', fontSize: '0.8rem', fontWeight: 600, background: 'white', color: '#a4161a', border: '1.5px solid #a4161a', borderRadius: 999, cursor: 'pointer' };
const lbl = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.8rem', fontWeight: 600, color: '#333' };
const inp = { padding: '0.4rem 0.5rem', border: '1px solid #ccc', borderRadius: 4, fontSize: '0.85rem', fontWeight: 400 };
const hint = { fontWeight: 400, color: '#777', fontSize: '0.75rem' };
const cardH = { margin: '0 0 0.5rem 0', fontSize: '1.05rem', color: '#000' };
const btnPrimary = { padding: '0.5rem 1rem', background: '#1565c0', color: 'white', border: '1px solid #0d47a1', borderRadius: 4, cursor: 'pointer', fontWeight: 600 };
const th = { textAlign: 'left', padding: '8px 10px', fontWeight: 700, color: '#333' };
const td = { padding: '8px 10px', verticalAlign: 'top' };
